import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { type FC, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'

import { getTemporaryId } from '@/utils/key.ts'

import '@/components/scrollbar.css'
import { onEnterInclusive } from '@/utils/keyboard.ts'

type Props = {
  items: ComboboxItem[]
  selectedItems: ComboboxItem[]
  SelectedItemComponent: FC<DisplayProps>
  SearchResultItemComponent: FC<SearchResultItemComponentProps>
  onSelect: (item: ComboboxItem) => void
  onRemove: (item: ComboboxItem) => void
  placeholder?: string
}

export const MultiSelectCombobox = ({
  items,
  selectedItems,
  SelectedItemComponent,
  SearchResultItemComponent,
  onSelect,
  onRemove,
  placeholder,
}: Props) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [highlightedItem, setHighlightedItem] = useState<ComboboxItem | null>(null)

  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  const filteredItems = useMemo(() => {
    const filtered = items
      .filter((item) => !selectedItems.some(({ id }) => id === item.id))
      .filter((item) => item.name.toLowerCase().includes(inputValue.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name))

    if (inputValue.trim() !== '' && !filtered.some((f) => f.name.toLowerCase() === inputValue.toLowerCase())) {
      return [{ id: getTemporaryId(), name: inputValue } as ComboboxItem, ...filtered]
    }

    return filtered
  }, [items, inputValue, selectedItems])

  useEffect(() => {
    if (filteredItems.length > 0) {
      setHighlightedIndex(0)
    }
  }, [filteredItems])

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
      })
    }
  }, [highlightedIndex])

  const handleSelect = (item: ComboboxItem) => {
    if (!selectedItems.some(({ name }) => name === item.name)) {
      onSelect(item)
    }
    setInputValue('')
    setHighlightedIndex(0)
    setOpen(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onEnterInclusive(event, () => {
        if (open && highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          handleSelect(filteredItems[highlightedIndex])
        }
        event.preventDefault()
      })
    } else if (open && event.key === 'ArrowDown') {
      setHighlightedIndex((prevIndex) => (prevIndex < filteredItems.length - 1 ? prevIndex + 1 : prevIndex))
      event.preventDefault()
    } else if (open && event.key === 'ArrowUp') {
      setHighlightedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex))
      event.preventDefault()
    } else if (event.key === 'Backspace' && !inputValue) {
      if (highlightedItem) {
        onRemove(highlightedItem)
        setHighlightedItem(null)
      } else if (selectedItems.length > 0) {
        setHighlightedItem(selectedItems[selectedItems.length - 1])
      }
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      if (inputValue.length > 0) return
      if (selectedItems.length > 0) {
        const currentIndex = highlightedItem ? selectedItems.indexOf(highlightedItem) : -1
        const newIndex =
          event.key === 'ArrowLeft'
            ? currentIndex > 0
              ? currentIndex - 1
              : selectedItems.length - 1
            : currentIndex < selectedItems.length - 1
              ? currentIndex + 1
              : 0
        setHighlightedItem(selectedItems[newIndex])
      }
    } else if (event.key === 'Escape') {
      setInputValue('')
      setOpen(false)
    }
  }

  const renderList = items.reduce((acc, item) => {
    if (!selectedItems.some(({ id }) => id === item.id)) return acc

    const newAcc = acc.slice()
    newAcc.push(item)
    return newAcc
  }, [] as ComboboxItem[])

  const setItemRef = (el: HTMLDivElement | null, index: number) => {
    if (el) {
      itemRefs.current[index] = el
    }
  }

  return (
    <div className="relative w-full ">
      <div className="flex min-h-8 gap-2 px-2 py-1 border border-border-default-neutral rounded-xs bg-surface-backgroundElevated focus-within:border-border-default-focused">
        <div className="flex flex-grow flex-wrap items-center gap-0.5">
          {renderList.map((item) => {
            return (
              <SelectedItemComponent
                key={item.id}
                variant="badge"
                item={item}
                onRemove={onRemove}
                isHighlighted={highlightedItem?.id === item.id}
              />
            )
          })}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setOpen(true)
              setHighlightedItem(null)
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (open) {
                setTimeout(() => {
                  setInputValue('')
                  setOpen(false)
                }, 200)
              }
            }}
            className="flex-1 outline-none bg-surface-backgroundElevated font-EN-KR-body2-regular"
            placeholder={renderList.length === 0 ? placeholder : ''}
          />
        </div>
        <Button
          aria-label="open list"
          type="button"
          size="icon"
          variant="ghost"
          className="flex-shrink-0"
          onClick={() => {
            setOpen((prev) => {
              const toggled = !prev
              if (toggled) {
                inputRef.current?.focus()
              }
              return toggled
            })
          }}
        >
          <img src="/images/chevron-down-navy-icon.svg" alt="more" />
        </Button>
      </div>
      {open && (
        <div className="absolute w-full z-10 bg-popover text-popover-foreground shadow-md mt-1 rounded-xs">
          <Command>
            <CommandList className="bg-surface-backgroundElevated py-1 pr-0.5 pl-0 tracker-scrollbar">
              <CommandEmpty>
                <div />
              </CommandEmpty>
              <CommandGroup className="p-0">
                {filteredItems.map((item, index) => (
                  <CommandItem
                    key={item.id}
                    ref={(el) => setItemRef(el, index)}
                    onSelect={() => handleSelect(item)}
                    className={cn(
                      'bg-surface-backgroundElevated rounded-none py-1.5 px-2 cursor-pointer',
                      index === highlightedIndex && 'bg-custom-onLight-hovered',
                    )}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <div className="flex items-center gap-1">
                      <img src="/images/label-icon.svg" alt="label icon" />
                      <SearchResultItemComponent item={item} variant="text" />
                    </div>
                    <Check
                      className={cn(
                        'ml-auto',
                        selectedItems.some(({ id }) => id === item.id) ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  )
}
